// recurrencia.js — expansión pura de reglas de recurrencia del calendario
// interno. Copia ES-module de netlify/functions/_recurrencia.js — MISMO
// cuerpo de función, mantener ambas copias sincronizadas si se cambia esta
// lógica (no hay puente Node↔navegador en este repo para compartir código
// entre netlify/functions y src).
//
// Los eventos recurrentes se guardan como una regla en el documento maestro
// (evento.recurrencia) y se expanden aquí a ocurrencias concretas dentro de
// un rango de fechas — no se materializa cada ocurrencia como documento.
// Las excepciones (edición/cancelación de una sola ocurrencia) son
// documentos aparte con seriePadreId + fechaOriginal, y se aplican aquí
// sobre las fechas candidatas generadas por la regla.

function parseISO(dateStr) {
  return new Date(dateStr + 'T12:00:00');
}
function toISO(date) {
  return date.toISOString().split('T')[0];
}
function addDays(dateStr, n) {
  const d = parseISO(dateStr);
  d.setDate(d.getDate() + n);
  return toISO(d);
}
function addMonthsClamped(dateStr, n) {
  const d = parseISO(dateStr);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + n);
  const lastDayDelMes = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, lastDayDelMes));
  return toISO(d);
}
function diaSemana(dateStr) {
  return parseISO(dateStr).getDay(); // 0 = domingo … 6 = sábado
}

// Genera las fechas (YYYY-MM-DD) en las que cae la regla de recurrencia de
// `evento`, acotadas al rango [rangoInicio, rangoFin] y a recurrencia.hasta.
function generarFechasCandidatas(evento, rangoInicio, rangoFin) {
  const { fechaInicio, recurrencia } = evento;
  const limite = recurrencia?.hasta && recurrencia.hasta < rangoFin ? recurrencia.hasta : rangoFin;
  if (!fechaInicio || fechaInicio > limite) return [];

  if (!recurrencia || recurrencia.frecuencia === 'ninguna') {
    return (fechaInicio >= rangoInicio && fechaInicio <= rangoFin) ? [fechaInicio] : [];
  }

  const intervalo = recurrencia.intervalo || 1;
  const fechas = [];

  if (recurrencia.frecuencia === 'diaria') {
    let f = fechaInicio;
    while (f <= limite) {
      if (f >= rangoInicio) fechas.push(f);
      f = addDays(f, intervalo);
    }
  } else if (recurrencia.frecuencia === 'semanal') {
    const diasSemana = recurrencia.diasSemana?.length ? recurrencia.diasSemana : [diaSemana(fechaInicio)];
    let inicioSemana = addDays(fechaInicio, -diaSemana(fechaInicio)); // domingo de esa semana
    let semana = 0;
    while (inicioSemana <= limite) {
      if (semana % intervalo === 0) {
        for (const dow of diasSemana) {
          const f = addDays(inicioSemana, dow);
          if (f >= fechaInicio && f >= rangoInicio && f <= limite) fechas.push(f);
        }
      }
      inicioSemana = addDays(inicioSemana, 7);
      semana++;
    }
    fechas.sort();
  } else if (recurrencia.frecuencia === 'mensual') {
    let i = 0;
    let f = fechaInicio;
    while (f <= limite) {
      if (f >= rangoInicio) fechas.push(f);
      i += intervalo;
      f = addMonthsClamped(fechaInicio, i);
    }
  }
  return fechas;
}

// Expande `evento` (documento maestro, o evento no recurrente) a ocurrencias
// concretas dentro de [rangoInicio, rangoFin], aplicando `excepciones`
// (array de documentos { fechaOriginal, excepcion: 'modificado'|'cancelado', ...camposSobrescritos }).
export function expandirRecurrencia(evento, excepciones, rangoInicio, rangoFin) {
  if (evento.estado === 'cancelado') return [];

  const fechas = generarFechasCandidatas(evento, rangoInicio, rangoFin);
  const excPorFecha = new Map();
  for (const exc of (excepciones || [])) excPorFecha.set(exc.fechaOriginal, exc);

  const eventoId = evento.id || evento._id;
  const ocurrencias = [];
  for (const fecha of fechas) {
    const exc = excPorFecha.get(fecha);
    if (exc?.excepcion === 'cancelado') continue;
    if (exc?.excepcion === 'modificado') {
      ocurrencias.push({ ...evento, ...exc, fechaOriginal: fecha, eventoId });
    } else {
      ocurrencias.push({ ...evento, fechaInicio: fecha, fechaFin: fecha, fechaOriginal: fecha, eventoId });
    }
  }
  return ocurrencias;
}

export { generarFechasCandidatas, addDays, addMonthsClamped, diaSemana };
